import { Component, type ReactNode } from "react";
import { PATHS } from "../lib/routes.js";
import { PageState } from "./page-state.js";

/**
 * Catches anything that throws while drawing outside a screen, and says so.
 *
 * The router catches each screen inside the rail (`defaultErrorComponent` in
 * `router.tsx`), which leaves the rail itself, sign-in and setup. Without this
 * one a throw there unmounted the whole app into a white page — no message,
 * nothing to click (`frontend.md` §5).
 *
 * A class, the one in this app, because React has no hook for this: only a
 * class component can be an error boundary. React reports what it caught to
 * the console itself, so nothing is logged twice here.
 */
export class ErrorBoundary extends Component<
  { readonly children: ReactNode },
  { readonly failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { readonly failed: boolean } {
    return { failed: true };
  }

  override render(): ReactNode {
    return this.state.failed ? <CrashPage /> : this.props.children;
  }
}

/**
 * The page shown in place of a screen that threw.
 *
 * Shared by this boundary, which catches the rail and sign-in, and by the
 * router, which catches each screen inside the rail (`router.tsx`).
 */
export function CrashPage() {
  return (
    <PageState
      title="This page broke while drawing"
      actions={
        <>
          <button
            type="button"
            className="button-primary"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload
          </button>
          {/* A full load, not an in-app move: after a crash, a clean start
              is the point, and the boundary above the rail has no address
              to reset on. */}
          <a href={PATHS.overview} className="button-secondary">
            Go to Overview
          </a>
        </>
      }
    >
      <p>
        Something went wrong on our side while showing this screen. Nothing was changed or
        lost. Reloading usually brings it back.
      </p>
    </PageState>
  );
}
